import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { FeatureFlags } from '@stigg-types/featureFlags';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useWorkflowLogin } from '../mutations/useWorkflowLogin';
import Loader from '../../../components/Loader';
import { Box } from '../../../components';
import config from '../../../env.config';
import { setIsDrawerExpanded, setIsNavbarHidden } from '../../application/applicationSlice';
import { RootState, useAppDispatch } from '../../../redux/store';
import { useSendTestWorkflowTriggerEvent } from '../mutations/sendTestWorkflowTriggerEvent';
import { WorkflowsRequestAccess } from './WorkflowsRequestAccess';

const WORKFLOW_VIEW_PATTERN = /^\/workflow\/[A-Za-z0-9]+\/*./;

function getWorkflowBaseUrl(billderBaseUrl?: string) {
  return billderBaseUrl || config.workflowsBaseUrl;
}

function getWorkflowUrl(baseUrl: string, workflowId: string) {
  return `${baseUrl}/workflow/${workflowId}`;
}

function getWorkflowExecutionUrl(baseUrl: string, workflowId: string, executionId: string) {
  return `${getWorkflowUrl(baseUrl, workflowId)}/executions/${executionId}`;
}

function getWorkflowQueryParams(queryParams: URLSearchParams) {
  const workflowId = queryParams.get('workflowId');
  const workflowExecutionId = queryParams.get('workflowExecutionId');

  return { workflowId, workflowExecutionId };
}

function getUrl(queryParams: URLSearchParams, projectId?: string, billderBaseUrl?: string): string {
  const { workflowId, workflowExecutionId } = getWorkflowQueryParams(queryParams);
  const baseUrl = getWorkflowBaseUrl(billderBaseUrl);

  if (workflowId && workflowExecutionId) {
    return getWorkflowExecutionUrl(baseUrl, workflowId, workflowExecutionId);
  } else if (workflowId) {
    return getWorkflowUrl(baseUrl, workflowId);
  } else {
    return `${baseUrl}/projects/${projectId}/workflows`;
  }
}

function getWorkflowParamsFromUrl(url: `/workflow/${string}/executions/${number}`): {
  workflowId: string;
  workflowExecutionId: string;
} {
  const [, scope, workflowId, subScope, workflowExecutionId] = url.split('/');

  if (scope !== 'workflow') return { workflowId: '', workflowExecutionId: '' };
  if (subScope !== 'executions') return { workflowId, workflowExecutionId: '' };

  return { workflowId, workflowExecutionId };
}

function handleRouteChangeQueryParams(path: string, queryParams: string) {
  const { workflowId, workflowExecutionId } = getWorkflowQueryParams(new URLSearchParams(queryParams));
  const { workflowId: workflowIdFromUrl, workflowExecutionId: workflowExecutionIdFromUrl } = getWorkflowParamsFromUrl(
    path as `/workflow/${string}/executions/${number}`,
  );

  if (workflowIdFromUrl === workflowId && workflowExecutionIdFromUrl === workflowExecutionId) return;

  const newQueryParams = new URLSearchParams();
  if (workflowIdFromUrl) newQueryParams.set('workflowId', workflowIdFromUrl);
  if (workflowExecutionIdFromUrl) newQueryParams.set('workflowExecutionId', workflowExecutionIdFromUrl);

  window.history.replaceState(
    {},
    '',
    `${window.location.pathname}${newQueryParams.toString() ? `?${newQueryParams.toString()}` : ''}`,
  );
}

export function WorkflowsPage() {
  const dispatch = useAppDispatch();
  const { billderRollout: workflowsHasAccess, billderBaseUrl } = useFlags<FeatureFlags>();
  const [isLoading, setIsLoading] = useState(true);
  const { data, isLoading: isLoginLoading } = useWorkflowLogin({ workflowsHasAccess });
  const { projectId, token } = data || {};
  const isDrawerExpandedState = useSelector((state: RootState) => state.application.isDrawerExpanded);
  const initialIsDrawerExpandedState = useRef(isDrawerExpandedState === 'true');
  const location = useLocation();
  const { mutate: sendTestEvent } = useSendTestWorkflowTriggerEvent();
  const environmentId = useSelector((state: RootState) => state.accountReducer.currentEnvironmentId) as string;

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);

        if (data.command === 'routeChange') {
          handleRouteChangeQueryParams(data.route.path, data.query);
          if (WORKFLOW_VIEW_PATTERN.test(data.route.fullPath)) {
            dispatch(setIsNavbarHidden(true));
            dispatch(setIsDrawerExpanded(false));
          } else {
            dispatch(setIsNavbarHidden(false));
            dispatch(setIsDrawerExpanded(initialIsDrawerExpandedState.current));
          }
        } else if (data.command === 'sendTestEvent') {
          const hookEventType = data.testEvent;
          const endpointUrl = data.endpointUrl;

          sendTestEvent({
            environmentId,
            endpointUrl,
            hookEventType,
          });
        }
        // eslint-disable-next-line no-empty
      } catch {}
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
      dispatch(setIsNavbarHidden(false));
      // eslint-disable-next-line react-hooks/exhaustive-deps
      dispatch(setIsDrawerExpanded(initialIsDrawerExpandedState.current));
    };
  }, [dispatch, environmentId, sendTestEvent]);

  if (!workflowsHasAccess) {
    return <WorkflowsRequestAccess />;
  }

  const queryParams = new URLSearchParams(location.search);
  const src = `${getUrl(queryParams, projectId, billderBaseUrl)}?token=${token}`;

  return (
    <>
      {!isLoginLoading && !!projectId && !!token && (
        <iframe onLoad={() => setIsLoading(false)} src={src} style={{ width: '100%', height: '100%', border: 0 }} />
      )}
      {isLoading && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          display="flex"
          justifyContent="center"
          alignItems="center">
          <Loader />
        </Box>
      )}
    </>
  );
}
